import React from 'react';
import {Link} from 'react-router-dom';
import {Icon} from '@lightricks/react-design-system';
import useIsMobile from '@/hooks/use-is-mobile';
import {GigShellData} from './shellTypes';
import {GigTabSection} from './gigTabs';
import GigStatusLabel from './GigStatusLabel';
import styles from './GigSidebar.module.scss';

interface GigSidebarProps {
  gigId: string;
  shell: GigShellData;
  sections: GigTabSection[];
  activeKind: string;
  /**
   * Gates the deliverables chrome. Ember wraps every deliverable link in
   * `{{#if @gig.inAcceptedState}}` (navigation.hbs:2-261) and leaves only the
   * Information tabs outside it, so an unaccepted gig gets no uploads target and
   * no progress bar for work that does not exist yet.
   */
  inAcceptedState: boolean;
  /** False while `totalApproved`/`totalRequired` cannot be trusted yet. */
  showProgress: boolean;
  needsActionCount: number;
  totalApproved: number;
  totalRequired: number;
  onSelectUploads: () => void;
  onToggleMessages: () => void;
}

function UploadsItem({
  activeKind,
  needsActionCount,
  onSelectUploads,
  className,
}: {
  activeKind: string;
  needsActionCount: number;
  onSelectUploads: () => void;
  className: string;
}) {
  const isActive = activeKind === 'deliverables';

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') e.preventDefault();
      onSelectUploads();
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${className}${isActive ? ` ${styles.tabActive}` : ''}`}
      data-testid="gig-nav-tab-uploads"
      aria-current={isActive ? 'true' : undefined}
      onClick={onSelectUploads}
      onKeyDown={handleKeyDown}
    >
      Deliverables
      {needsActionCount > 0 && (
        <span className={styles.badge} data-testid="uploads-badge">
          {needsActionCount}
        </span>
      )}
    </div>
  );
}

function GigSidebar({
  gigId: _gigId,
  shell,
  sections,
  activeKind,
  inAcceptedState,
  showProgress,
  needsActionCount,
  totalApproved,
  totalRequired,
  onSelectUploads,
  onToggleMessages,
}: GigSidebarProps) {
  const isMobile = useIsMobile();
  const showUnreadBadge = shell.hasUnreadConversations;
  const progressPct = totalRequired
    ? Math.min(100, (totalApproved / totalRequired) * 100)
    : 0;

  function handleMessagesKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      if (e.key === ' ') e.preventDefault();
      onToggleMessages();
    }
  }

  const campaignBlock = (
    <div className={styles.campaign}>
      <div
        className={styles.campaignImage}
        style={
          shell.imageUrl
            ? {backgroundImage: `url("${shell.imageUrl}")`}
            : undefined
        }
      />
      <div className={styles.campaignDetails}>
        <div className={styles.merchantName}>{shell.merchantName}</div>
        <div className={styles.campaignName}>{shell.campaignName}</div>
      </div>
    </div>
  );

  const messagesBlock = shell.hasMessages ? (
    <div
      role="button"
      tabIndex={0}
      aria-label={showUnreadBadge ? 'Open messages (unread)' : 'Open messages'}
      className={styles.messages}
      data-testid="gig-messages"
      onClick={onToggleMessages}
      onKeyDown={handleMessagesKeyDown}
    >
      <div
        className={`${styles.messagesIcon}${
          showUnreadBadge ? ` ${styles.messagesIconUnread}` : ''
        }`}
      >
        {showUnreadBadge && (
          <span
            className={styles.unreadDot}
            data-testid="gig-messages-unread"
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  ) : null;

  const progressBlock =
    !inAcceptedState || !showProgress ? null : (
      <div className={styles.progress} data-testid="gig-progress">
        <div className={styles.progressTop}>
          <span className={styles.progressLabel}>Deliverables done</span>
          <span className={styles.progressCount}>
            {totalApproved} / {totalRequired}
          </span>
        </div>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{width: `${progressPct}%`}}
          />
        </div>
      </div>
    );

  if (isMobile) {
    const allTabs = sections.flatMap((s) => s.tabs);

    return (
      <div className={styles.mobileContainer}>
        <div className={styles.mobileHeader}>
          <div className={styles.mobileHeaderTop}>
            <Link
              to={`/gigs/your-gigs/${shell.gigScope}`}
              className={styles.backLink}
              data-testid="gig-back"
              aria-label="Back to your gigs"
            >
              <Icon
                name="Actions-Arrow-Back-Large-Primary"
                size="medium"
                appearance="neutral"
              />
            </Link>
            {campaignBlock}
            {messagesBlock}
          </div>
          <GigStatusLabel
            state={shell.state}
            networkName={shell.socialNetworkName}
          />
          {progressBlock}
        </div>
        <nav className={styles.mobileTabs}>
          {inAcceptedState && (
            <UploadsItem
              activeKind={activeKind}
              needsActionCount={needsActionCount}
              onSelectUploads={onSelectUploads}
              className={styles.mobileTab}
            />
          )}
          {allTabs.map((tab) => (
            <Link
              key={tab.key}
              to={tab.to}
              className={`${styles.mobileTab}${
                tab.active ? ` ${styles.tabActive}` : ''
              }`}
              data-testid={`gig-nav-tab-${tab.key}`}
              data-active={tab.active ? 'true' : undefined}
              aria-current={tab.active ? 'true' : undefined}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <aside className={styles.sidebar}>
      <Link
        to={`/gigs/your-gigs/${shell.gigScope}`}
        className={styles.backLink}
        data-testid="gig-back"
        aria-label="Back to your gigs"
      >
        <Icon
          name="Actions-Arrow-Back-Large-Primary"
          size="medium"
          appearance="neutral"
        />
        <span>Your Gigs</span>
      </Link>

      <div className={styles.campaignRow}>
        {campaignBlock}
        {messagesBlock}
      </div>

      <GigStatusLabel
        state={shell.state}
        networkName={shell.socialNetworkName}
      />

      {progressBlock}

      <nav className={styles.sections}>
        {inAcceptedState && (
          <UploadsItem
            activeKind={activeKind}
            needsActionCount={needsActionCount}
            onSelectUploads={onSelectUploads}
            className={styles.tab}
          />
        )}

        {sections.map((section, idx) => {
          const isInformation = section.key === 'information';
          const prevSection = sections[idx - 1];
          const showDivider = isInformation && idx > 0 && prevSection != null;

          return (
            <React.Fragment key={section.key}>
              {showDivider && <div className={styles.divider} />}
              <div className={styles.sectionLabel}>{section.label}</div>
              {section.tabs.map((tab) => (
                <Link
                  key={tab.key}
                  to={tab.to}
                  className={`${styles.tab}${
                    tab.active ? ` ${styles.tabActive}` : ''
                  }`}
                  data-testid={`gig-nav-tab-${tab.key}`}
                  data-active={tab.active ? 'true' : undefined}
                  aria-current={tab.active ? 'true' : undefined}
                >
                  {tab.label}
                  {tab.required != null && (
                    <span className={styles.tabBadge}>
                      {tab.accepted ?? 0} | {tab.required}
                    </span>
                  )}
                  {tab.showActionIndicator && (
                    <span
                      className={styles.actionIndicator}
                      data-testid="action-indicator"
                      aria-hidden="true"
                    />
                  )}
                </Link>
              ))}
            </React.Fragment>
          );
        })}
      </nav>
    </aside>
  );
}

export default GigSidebar;
